import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  hideTitle?: boolean;
  hideClose?: boolean;
}

const sizeMap = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
} as const;

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
  hideTitle,
  hideClose,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-parchment-ink/40 animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100vw-2rem)] rounded-lg bg-parchment-light p-4 shadow-parchment-modal sm:p-6',
            // dvh, not vh: mobile browser chrome eats part of the visual viewport
            // and a 90vh sheet would push its own footer under the URL bar.
            'animate-modal-in max-h-[85dvh] overflow-y-auto',
            sizeMap[size],
          )}
        >
          <div className={cn('mb-4 flex items-start justify-between gap-4', hideTitle && 'sr-only')}>
            <div className="flex-1">
              <Dialog.Title className="font-display text-2xl text-parchment-ink">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-parchment-muted">
                  {description}
                </Dialog.Description>
              )}
            </div>
            {!hideClose && (
              <Dialog.Close
                className="rounded-md p-1.5 text-parchment-muted coarse:p-3.5 hover:bg-parchment-dark hover:text-parchment-ink"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Dialog.Close>
            )}
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const ModalFooter = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function ModalFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
        {...rest}
      />
    );
  },
);
